import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import * as api from '../api.ts';
import {
  Button,
  Cell,
  Empty,
  Field,
  Finding,
  Label,
  LoadError,
  Row,
  Section,
  Segmented,
  Spinner,
  Status,
  Table,
} from '../ui.tsx';

/**
 * Connecting a Slack or Teams channel, as a guided flow rather than a bare box.
 *
 * The webhook write path shipped with migration 0024, and Settings could paste
 * a URL into it — but "paste an incoming-webhook URL" assumes the admin has one
 * and knows where it comes from, and Huseyin's enterprise ask is the opposite:
 * the value is walking somebody through *getting* it. So this is the one place
 * in Settings that is a wizard rather than a plain form — a first-run flow a
 * person does once per channel, which is exactly the line `settings.tsx` draws
 * for when a wizard earns its place.
 *
 * The flow: pick the platform, find the webhook (the platform-specific steps,
 * with a link to the provider's own page), paste the URL and name the channel,
 * then send a harmless test and watch it land. The connected list underneath
 * stays a plain settings surface — change drills, remove — because those are
 * the "change one thing safely" edits the rest of the screen is built for.
 *
 * **The URL is a credential and it flows one way.** It is validated for shape
 * here, sealed by the server on arrival, and never returned by any endpoint —
 * the list shows the admin's own label, never the webhook.
 */

type Kind = 'slack_webhook' | 'teams_webhook';

/**
 * The URL must be the platform's own webhook host, checked here before it is
 * ever sent.
 *
 * This is not security — the server enforces the same rule (`channels.ts`
 * `endpointHostAllowed`), and an admin already holds the org — but the ordinary
 * paste error: a channel page URL, an internal wiki link, the app-management
 * page instead of the webhook. Caught here, the person is told at the field
 * rather than after a round trip. Teams accepts two hosts because Microsoft
 * retired Office 365 connectors in favour of Workflows, which serve from Logic
 * Apps domains.
 */
export function webhookHostValid(kind: Kind, endpoint: string): boolean {
  let url: URL;
  try {
    url = new URL(endpoint.trim());
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  if (kind === 'slack_webhook') return url.hostname === 'hooks.slack.com';
  return url.hostname.endsWith('.webhook.office.com') || url.hostname.endsWith('.logic.azure.com');
}

/** What a wrong host should say, in the platform's own terms. */
export function webhookHostHint(kind: Kind): string {
  return kind === 'slack_webhook'
    ? 'A Slack incoming webhook lives on https://hooks.slack.com/… — paste that, not the channel or app page.'
    : 'A Teams webhook lives on https://…webhook.office.com or …logic.azure.com — paste the workflow URL, not the channel link.';
}

/** The provider's own setup documentation, per platform. */
const SETUP: Record<Kind, { label: string; href: string; steps: string[] }> = {
  slack_webhook: {
    label: 'Slack',
    href: 'https://api.slack.com/messaging/webhooks',
    steps: [
      'Open api.slack.com/apps and create an app (or open an existing one) in your workspace.',
      'Under Features, choose Incoming Webhooks and turn Activate Incoming Webhooks on.',
      'Choose Add New Webhook to Workspace, then pick the channel alerts should post to.',
      'Copy the webhook URL Slack shows — it begins https://hooks.slack.com/services/…',
    ],
  },
  teams_webhook: {
    label: 'Teams',
    href: 'https://support.microsoft.com/en-us/office/create-incoming-webhooks-with-workflows-for-microsoft-teams-8ae491c7-0394-4861-ba59-055e33f75498',
    steps: [
      'In Teams, open the channel alerts should post to, then its ⋯ menu → Workflows.',
      'Choose the template “Post to a channel when a webhook request is received”.',
      'Confirm the team and channel, then create the workflow.',
      'Copy the URL it generates — it ends in webhook.office.com or logic.azure.com.',
    ],
  },
};

type Step = 'platform' | 'find' | 'paste' | 'test';
const STEP_ORDER: Step[] = ['platform', 'find', 'paste', 'test'];
const STEP_LABEL: Record<Step, string> = {
  platform: 'Platform',
  find: 'Find the webhook',
  paste: 'Paste & name',
  test: 'Send a test',
};

/**
 * Where the person is in the flow. An ordered list rather than decoration, so a
 * screen reader announces the current step; colour is never the only carrier
 * (§7 greyscale rule) — the current step is bold and marked with a filled dot,
 * done steps read "done" to assistive tech.
 */
function Steps({ current }: { current: Step }) {
  const at = STEP_ORDER.indexOf(current);
  return (
    <ol aria-label="Connect a channel — progress" className="mb-5 flex flex-wrap gap-x-5 gap-y-1">
      {STEP_ORDER.map((s, i) => {
        const state = i < at ? 'done' : i === at ? 'current' : 'upcoming';
        return (
          <li key={s} aria-current={state === 'current' ? 'step' : undefined}>
            <Status tone={state === 'upcoming' ? 'neutral' : 'safe'}>
              <span
                className={
                  state === 'current' ? 'font-[650] text-[var(--ink)]' : 'text-[var(--ink-2)]'
                }
              >
                {i + 1}. {STEP_LABEL[s]}
                {state === 'done' ? <span className="sr-only"> (done)</span> : null}
              </span>
            </Status>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The connected list, and the guided flow to add to it.
 *
 * Exported so the flow can be mounted and driven in a test without the whole
 * Settings screen and its billing, template and audit queries.
 */
export function Channels({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const channels = useQuery({
    queryKey: ['channels', orgId],
    queryFn: () => api.getChannels(orgId),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['channels', orgId] });

  // null when the flow is closed and the list is at rest.
  const [step, setStep] = useState<Step | null>(null);
  const [kind, setKind] = useState<Kind>('slack_webhook');
  const [endpoint, setEndpoint] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [addedId, setAddedId] = useState<string | null>(null);
  const [tested, setTested] = useState<'idle' | 'ok' | 'failed'>('idle');
  const [testError, setTestError] = useState<string | null>(null);

  function reset() {
    setStep(null);
    setKind('slack_webhook');
    setEndpoint('');
    setLabel('');
    setError(null);
    setAddedId(null);
    setTested('idle');
    setTestError(null);
  }

  /**
   * Start the guided flow for one platform, from the integration list. The
   * platform is already chosen (it is the row that was clicked), so the flow
   * opens at the "find the webhook" step rather than the platform picker — the
   * same wizard, one step in.
   */
  function beginConnect(k: Kind) {
    setKind(k);
    setEndpoint('');
    setLabel('');
    setError(null);
    setAddedId(null);
    setTested('idle');
    setTestError(null);
    setStep('find');
  }

  const add = useMutation({
    mutationFn: () =>
      api.addChannel(orgId, { kind, endpoint: endpoint.trim(), label: label.trim() || undefined }),
    onSuccess: (res) => {
      setError(null);
      setAddedId(res?.channelId ?? null);
      invalidate();
      setStep('test');
    },
    onError: (e: Error) => {
      if (e instanceof api.ApiError && e.status === 402) {
        setError('Connecting a channel needs a paid plan. Upgrade above, then try again.');
      } else if (e instanceof api.ApiError && e.status === 409) {
        setError('This webhook is already connected. Every alert would arrive twice.');
      } else if (e instanceof api.ApiError && e.status === 403) {
        setError('For your security this needs a fresh sign-in. Sign out and back in, then retry.');
      } else {
        setError(e.message);
      }
    },
  });

  const test = useMutation({
    mutationFn: (id: string) => api.testChannel(orgId, id),
    onSuccess: () => {
      setTested('ok');
      setTestError(null);
    },
    onError: (e: Error) => {
      setTested('failed');
      setTestError(
        e instanceof api.ApiError && e.status === 502
          ? 'The message did not reach the channel. Check the webhook is still active in ' +
              `${SETUP[kind].label}, then reconnect.`
          : e.message,
      );
    },
  });

  const toggleDrills = useMutation({
    mutationFn: ({ id, drills }: { id: string; drills: boolean }) =>
      api.updateChannel(orgId, id, { drillsIncluded: drills }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.removeChannel(orgId, id),
    onSuccess: invalidate,
  });

  // A per-row test, so a channel connected months ago can be proven again
  // without reconnecting — the same endpoint the flow's test step calls.
  const rowTest = useMutation({
    mutationFn: (id: string) => api.testChannel(orgId, id),
  });

  return (
    <Section title="Slack and Teams">
      <p className="mb-4 max-w-[640px] text-[14px] text-[var(--ink-2)]">
        Post alerts into a channel your organisation already lives in — no app to install. A channel
        is a room, not a person: nothing posted here counts toward accountability, only the confirm
        link inside the message does. The webhook URL is stored encrypted and never shown again.
      </p>

      {/*
        The integrations as a status list rather than one-off blocks (owner review
        2026-09-07): each platform is a row showing whether it is connected and the
        action to connect it. The pattern is open for more integrations — add a
        `Kind` and its `SETUP` entry and a row appears. Connecting opens the same
        guided flow, one step in (the platform is the row you clicked).
      */}
      {channels.data ? (
        <div className="mb-6">
          <Table head={['Integration', 'Status', '']}>
            {(['slack_webhook', 'teams_webhook'] as Kind[]).map((k) => {
              const connected = channels.data!.channels.filter(
                (c) => c.kind === k && c.active,
              ).length;
              return (
                <Row key={k}>
                  <Cell>{SETUP[k].label}</Cell>
                  <Cell wide>
                    {connected > 0 ? (
                      <Status tone="safe">
                        Connected{connected > 1 ? ` · ${connected} channels` : ''}
                      </Status>
                    ) : (
                      <Status tone="neutral">Not connected</Status>
                    )}
                  </Cell>
                  <Cell>
                    <Button
                      variant={connected > 0 ? 'quiet' : 'secondary'}
                      label={
                        connected > 0
                          ? `Connect another ${SETUP[k].label} channel`
                          : `Connect ${SETUP[k].label}`
                      }
                      onClick={() => beginConnect(k)}
                    >
                      {connected > 0 ? 'Add another' : 'Connect'}
                    </Button>
                  </Cell>
                </Row>
              );
            })}
          </Table>
        </div>
      ) : null}

      {channels.isError && !channels.data ? (
        <LoadError onRetry={() => void channels.refetch()}>
          Couldn’t load the connected channels. This is a load error, not an empty list — retry.
        </LoadError>
      ) : channels.isLoading || !channels.data ? (
        <Spinner />
      ) : channels.data.channels.length === 0 ? (
        step === null ? (
          // No second CTA here: the integrations status list above already
          // carries a Connect action per platform, so a "Connect a channel"
          // button in this empty block would be a redundant second entry point
          // into the same wizard (owner review 2026-09-07, L4).
          <Empty>
            No channels yet. Connect a Slack or Teams channel above to post alerts where your people
            already are.
          </Empty>
        ) : null
      ) : (
        <>
          <Table head={['Channel', 'Platform', 'Drills', '']}>
            {channels.data.channels.map((c) => (
              <Row key={c.id}>
                <Cell wide>{c.label ?? 'Unlabelled'}</Cell>
                <Cell>{c.kind === 'slack_webhook' ? 'Slack' : 'Teams'}</Cell>
                <Cell>
                  <Button
                    variant="quiet"
                    label={`${c.drills_included ? 'Stop sending' : 'Send'} drills to ${c.label ?? 'this channel'}`}
                    onClick={() => toggleDrills.mutate({ id: c.id, drills: !c.drills_included })}
                  >
                    {c.drills_included ? 'Drills on' : 'Drills off'}
                  </Button>
                </Cell>
                <Cell>
                  <span className="flex items-center gap-1">
                    <Button
                      variant="quiet"
                      label={`Send a test message to ${c.label ?? 'this channel'}`}
                      disabled={rowTest.isPending && rowTest.variables === c.id}
                      onClick={() => rowTest.mutate(c.id)}
                    >
                      {rowTest.isSuccess && rowTest.variables === c.id
                        ? 'Test sent'
                        : rowTest.isError && rowTest.variables === c.id
                          ? 'Test failed'
                          : 'Send test'}
                    </Button>
                    <Button
                      variant="quiet"
                      label={`Remove ${c.label ?? 'this channel'}`}
                      onClick={() => remove.mutate(c.id)}
                    >
                      Remove
                    </Button>
                  </span>
                </Cell>
              </Row>
            ))}
          </Table>
          {rowTest.isError ? (
            <div className="mt-3">
              <Finding>
                A test message did not reach that channel. Check the webhook is still active, then
                reconnect it.
              </Finding>
            </div>
          ) : null}
          {step === null ? (
            <div className="mt-4">
              <Button variant="secondary" onClick={() => setStep('platform')}>
                Connect another channel
              </Button>
            </div>
          ) : null}
        </>
      )}

      {step !== null ? (
        <div className="mt-6 max-w-[640px] border-t border-[var(--line)] pt-5">
          <Steps current={step} />

          {step === 'platform' ? (
            <div>
              <div className="mb-1.5">
                <Label>Which platform</Label>
              </div>
              <Segmented<Kind>
                label="Which platform to connect"
                value={kind}
                onChange={setKind}
                options={[
                  { value: 'slack_webhook', label: 'Slack' },
                  { value: 'teams_webhook', label: 'Teams' },
                ]}
              />
              <p className="mt-3 text-[14px] text-[var(--ink-2)]">
                Both connect through an incoming webhook — a URL the platform gives you that lets
                EVAC post into one channel.
              </p>
              <div className="mt-5 flex gap-3">
                <Button variant="primary" onClick={() => setStep('find')}>
                  Next
                </Button>
                <Button variant="quiet" onClick={reset}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}

          {step === 'find' ? (
            <div>
              <h3 className="text-[15px] leading-5 font-[650]">
                Create the {SETUP[kind].label} incoming webhook
              </h3>
              <ol className="mt-3 list-decimal space-y-2 pl-5 text-[14px] leading-[21px] text-[var(--ink-2)]">
                {SETUP[kind].steps.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ol>
              <p className="mt-3 text-[14px]">
                <a
                  href={SETUP[kind].href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--accent)] underline underline-offset-2 hover:no-underline"
                >
                  Open {SETUP[kind].label}’s setup guide
                  <span aria-hidden> ↗</span>
                </a>
              </p>
              <div className="mt-5 flex gap-3">
                <Button variant="primary" onClick={() => setStep('paste')}>
                  I have the URL
                </Button>
                <Button variant="secondary" onClick={() => setStep('platform')}>
                  Back
                </Button>
                <Button variant="quiet" onClick={reset}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}

          {step === 'paste' ? (
            <div>
              <div className="grid gap-4">
                <Field
                  label={`${SETUP[kind].label} webhook URL`}
                  value={endpoint}
                  onChange={(v) => {
                    setEndpoint(v);
                    setError(null);
                  }}
                  placeholder={
                    kind === 'slack_webhook'
                      ? 'https://hooks.slack.com/services/…'
                      : 'https://…webhook.office.com/…'
                  }
                  hint="Pasted once and sealed. It is never shown again."
                />
                <Field
                  label="Name this channel"
                  value={label}
                  onChange={setLabel}
                  placeholder="#emergencies"
                  hint="A human label for the connected list. The URL is never shown."
                />
              </div>
              {error ? (
                <div className="mt-3">
                  <Finding>{error}</Finding>
                </div>
              ) : null}
              <div className="mt-5 flex gap-3">
                <Button
                  variant="primary"
                  disabled={!endpoint.trim() || add.isPending}
                  onClick={() => {
                    if (!webhookHostValid(kind, endpoint)) {
                      setError(webhookHostHint(kind));
                      return;
                    }
                    add.mutate();
                  }}
                >
                  {add.isPending ? 'Connecting…' : 'Connect channel'}
                </Button>
                <Button variant="secondary" onClick={() => setStep('find')}>
                  Back
                </Button>
                <Button variant="quiet" onClick={reset}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}

          {step === 'test' ? (
            <div>
              <Finding tone="safe">
                <Status tone="safe">Channel connected</Status>{' '}
                <span className="text-[var(--ink-2)]">
                  {label.trim()
                    ? `${label.trim()} is on the list below.`
                    : 'It is on the list below.'}
                </span>
              </Finding>
              <p className="mt-3 max-w-prose text-[14px] text-[var(--ink-2)]">
                Send a test message to confirm it lands where you expect. Drills are off for this
                channel until you switch them on — real emergency alerts always arrive.
              </p>
              {tested === 'ok' ? (
                <div className="mt-3">
                  <Finding tone="safe">
                    Test message sent. Check {SETUP[kind].label} — you should see it in the channel.
                  </Finding>
                </div>
              ) : null}
              {tested === 'failed' && testError ? (
                <div className="mt-3">
                  <Finding>{testError}</Finding>
                </div>
              ) : null}
              <div className="mt-5 flex gap-3">
                {addedId ? (
                  <Button
                    variant={tested === 'ok' ? 'secondary' : 'primary'}
                    disabled={test.isPending}
                    onClick={() => test.mutate(addedId)}
                  >
                    {test.isPending
                      ? 'Sending…'
                      : tested === 'ok'
                        ? 'Send another test'
                        : 'Send test message'}
                  </Button>
                ) : null}
                <Button variant={tested === 'ok' ? 'primary' : 'secondary'} onClick={reset}>
                  Done
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </Section>
  );
}
